/*
 * App shell: session, repo selection, sync, filters, theme.
 *
 * The sync is explicit (a button, not a poller) because it is a burst of
 * GitHub API budget and the user should decide when to spend it. Relative times
 * still tick every 30s, exactly as the original component did.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ApiError, type Me, type RepoHealth, type RepoState, type RepoSummary } from './types'
import { getMe, getRepoHealth, getRepos, logout, syncRepo } from './lib/api'
import { copyText, repoDiagnostics } from './lib/diagnostics'
import { CACHE_TTL_MS, clearCache, readCache, readLastRepo, writeCache, writeLastRepo } from './lib/cache'
import { num, rel } from './lib/format'
import { Archive, Clipboard, GitBranch, Lock, Moon, SignOut, Sun, Sync, Warning } from './components/Icons'
import { Avatar } from './components/Bits'
import { RepoPicker } from './components/RepoPicker'
import { Pipeline } from './components/Pipeline'
import { BranchTable } from './components/BranchTable'
import { PullRequests } from './components/PullRequests'
import { SignIn } from './components/SignIn'
import { Databases } from './components/Databases'
import { Diagnostics } from './components/Diagnostics'

type Filter = 'all' | 'env' | 'work'
const FILTERS: { id: Filter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'env', label: 'Environments' },
  { id: 'work', label: 'Work branches' }
]

type View = 'repo' | 'databases' | 'diagnostics'
const VIEWS: { id: View; label: string }[] = [
  { id: 'repo', label: 'Repository' },
  { id: 'databases', label: 'Databases' },
  { id: 'diagnostics', label: 'Diagnostics' }
]

type Theme = 'dark' | 'light'

function useTheme(): [Theme, () => void] {
  const [theme, setTheme] = useState<Theme>(() => {
    try { return (localStorage.getItem('om-bv-theme') as Theme) || 'dark' } catch { return 'dark' }
  })
  useEffect(() => {
    document.documentElement.dataset.theme = theme
    document.documentElement.style.background = theme === 'light' ? '#e4e7f5' : '#161826'
    try { localStorage.setItem('om-bv-theme', theme) } catch { /* ignore */ }
  }, [theme])
  return [theme, () => setTheme(t => (t === 'light' ? 'dark' : 'light'))]
}

/** Reads ?auth_error= left behind by a failed OAuth callback, then cleans the URL. */
function useAuthError(): string | null {
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const value = params.get('auth_error')
    if (!value) return
    setError(value)
    params.delete('auth_error')
    const qs = params.toString()
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''))
  }, [])
  return error
}

export default function App() {
  const [theme, toggleTheme] = useTheme()
  const authError = useAuthError()

  const [me, setMe] = useState<Me | null>(null)
  const [repos, setRepos] = useState<RepoSummary[]>([])
  const [reposLoading, setReposLoading] = useState(false)
  const [selected, setSelected] = useState<string | null>(null)
  const [state, setState] = useState<RepoState | null>(null)
  const [syncing, setSyncing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<Filter>('all')
  const [view, setView] = useState<View>('repo')
  const [health, setHealth] = useState<RepoHealth | null>(null)
  const [checking, setChecking] = useState(false)
  const [copied, setCopied] = useState(false)
  const [, setTick] = useState(0)
  const inflight = useRef<string | null>(null)

  // Relative times are recomputed, not stored, so a repaint is all it takes.
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 30_000)
    return () => clearInterval(id)
  }, [])

  useEffect(() => { getMe().then(setMe).catch(() => setMe({ configured: true, org: 'F3-Insights', authenticated: false, user: null })) }, [])

  const signedIn = !!me?.authenticated

  useEffect(() => {
    if (!signedIn) return
    setReposLoading(true)
    getRepos()
      .then(({ repos }) => {
        setRepos(repos)
        const remembered = readLastRepo()
        const fallback = remembered && repos.some(r => r.name === remembered) ? remembered : repos[0]?.name
        setSelected(current => current ?? fallback ?? null)
      })
      .catch((err: ApiError) => setError(err.message))
      .finally(() => setReposLoading(false))
  }, [signedIn])

  const runSync = useCallback(async (repo: string, { force = false } = {}) => {
    if (inflight.current === repo) return
    if (!force) {
      const cached = readCache(repo)
      if (cached) { setState(cached); setError(null); return }
    }
    inflight.current = repo
    setSyncing(true)
    setError(null)
    try {
      const fresh = await syncRepo(repo)
      writeCache(repo, fresh)
      setState(fresh)
    } catch (err) {
      const apiErr = err as ApiError
      if (apiErr.unauthenticated) { setMe(m => (m ? { ...m, authenticated: false, user: null } : m)); clearCache() }
      setError(apiErr.message)
      setState(null)
    } finally {
      inflight.current = null
      setSyncing(false)
    }
  }, [])

  useEffect(() => {
    if (!selected) return
    writeLastRepo(selected)
    runSync(selected)
  }, [selected, runSync])

  /* Probing is deliberately not part of the sync and not cached: a cached
   * "up" is a claim about the past, and the whole point of the badge is that
   * it is true now. Stale results from the previously selected repo are
   * dropped rather than shown against the new one. */
  const runHealth = useCallback(async (repo: string) => {
    setChecking(true)
    setHealth(null)
    try {
      const fresh = await getRepoHealth(repo)
      setHealth(current => (fresh.repo === repo ? fresh : current))
    } catch {
      // A failed probe run is not a failed page. The badges stay unresolved.
      setHealth(null)
    } finally {
      setChecking(false)
    }
  }, [])

  useEffect(() => {
    if (!selected) return
    let cancelled = false
    setHealth(null)
    runHealth(selected).then(() => { if (cancelled) setHealth(null) })
    return () => { cancelled = true }
  }, [selected, runHealth])

  const branches = useMemo(() => {
    if (!state) return []
    return state.branches.filter(b => (filter === 'env' ? !!b.env : filter === 'work' ? !b.env : true))
  }, [state, filter])

  const stale = state ? Date.now() - new Date(state.fetchedAt).getTime() > CACHE_TTL_MS : false

  if (me && (!me.authenticated || !me.configured)) {
    return <SignIn org={me.org} configured={me.configured} error={authError || error} />
  }

  return (
    <div className="page">
      <div className="shell">
        <header className="appbar">
          <div className="brand">
            <span className="brand-mark"><GitBranch size={20} /></span>
            <span className="brand-name">Repository State</span>
            <span className="brand-org">· {me?.org ?? 'F3-Insights'}</span>
          </div>
          <div className="appbar-right">
            {me?.user ? (
              <span className="userchip">
                <Avatar person={{ alias: me.user.login, name: me.user.name, avatar: me.user.avatar }} />
                {me.user.login}
              </span>
            ) : null}
            <button
              className="iconbtn"
              onClick={toggleTheme}
              title="Toggle light / dark mode"
              aria-label="Toggle light or dark mode"
            >
              {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
            </button>
            <button
              className="iconbtn"
              title="Sign out"
              aria-label="Sign out"
              onClick={async () => { await logout(); clearCache(); window.location.reload() }}
            >
              <SignOut size={16} />
            </button>
          </div>
        </header>

        <nav className="viewtabs">
          {VIEWS.map(v => (
            <button
              key={v.id}
              type="button"
              className={`viewtab ${view === v.id ? 'on' : ''}`}
              onClick={() => setView(v.id)}
              aria-current={view === v.id ? 'page' : undefined}
            >
              {v.label}
            </button>
          ))}
        </nav>

        {view === 'databases' ? (
          <Databases />
        ) : view === 'diagnostics' ? (
          <Diagnostics />
        ) : (
          <>
            {error ? (
              <div className="banner">
                <Warning size={16} />
                <span className="body"><strong>Sync failed.</strong> {error}</span>
              </div>
            ) : null}

            <div className="repohead">
              <div style={{ minWidth: 0 }}>
                <div className="repotitle">
                  <RepoPicker repos={repos} value={selected} onSelect={setSelected} loading={reposLoading} />
                  {state ? (
                    <>
                      <span className="tag tag-outline">{stale ? 'cached' : state.sourceLabel}</span>
                      {state.private ? <span className="lock" title="Private repository"><Lock size={13} /></span> : null}
                      {state.archived ? <span className="tag tag-neutral"><Archive size={11} /> archived</span> : null}
                    </>
                  ) : null}
                </div>
                <div className="submeta">
                  {state ? (
                    <>
                      Branches, commits, pull requests and checks
                      <span className="sep">·</span>updated {rel(state.fetchedAt)}
                      <span className="sep">·</span>{num(state.branchCount)} branches
                      <span className="sep">·</span>{num(state.prCount)} open PR{state.prCount === 1 ? '' : 's'}
                      {state.rate ? <><span className="sep">·</span>{num(state.rate.remaining)} API points left</> : null}
                    </>
                  ) : syncing ? 'Syncing…' : 'Pick a repository to sync.'}
                </div>
              </div>

              <div className="repohead-actions">
                <div className="seg">
                  {FILTERS.map(f => (
                    <button key={f.id} className={filter === f.id ? 'on' : ''} onClick={() => setFilter(f.id)}>
                      {f.label}
                    </button>
                  ))}
                </div>
                <button
                  className="btn btn-ghost"
                  disabled={!state}
                  title="Copy a paste-ready diagnostics report for this repository"
                  onClick={async () => {
                    if (!state) return
                    const ok = await copyText(repoDiagnostics(state, health))
                    setCopied(ok)
                    if (ok) setTimeout(() => setCopied(false), 2500)
                  }}
                >
                  <Clipboard size={15} />
                  {copied ? 'Copied' : 'Diagnostics'}
                </button>
                <button
                  className="btn"
                  onClick={() => { if (selected) { runSync(selected, { force: true }); runHealth(selected) } }}
                  disabled={!selected || syncing}
                  title="Fetch current state from GitHub and re-probe the environments"
                >
                  <Sync size={15} className={syncing ? 'spin' : undefined} />
                  {syncing ? 'Syncing…' : 'Sync'}
                </button>
              </div>
            </div>

            {state ? (
              <>
                <Pipeline state={state} animate={!syncing} health={health?.environments} checking={checking} />
                <BranchTable branches={branches} total={state.branchCount} truncated={state.truncatedBranches} />
                <PullRequests prs={state.pullRequests} total={state.prCount} />
                <div className="note">
                  Synced live from GitHub as <code>{state.syncedBy}</code> in {state.syncMs}ms
                  {state.hasOverride ? <> · environment mapping from <code>.github/branch-viz.json</code></> : null}
                  {' · '}
                  <a href={state.url} target="_blank" rel="noreferrer noopener">open on GitHub</a>
                </div>
              </>
            ) : !error ? (
              <section className="section">
                <div className="empty">{syncing ? 'Reading branches, commits, pull requests and checks…' : 'Select a repository to begin.'}</div>
              </section>
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}
